// Shared notes-fetching + note-list rendering — extracted out of
// OrderNotesWindow.jsx so DocumentDetailWindow.jsx can show the same TRQ
// notes read (routes/conversations.js's GET /:id/notes, a deliberately
// fresh/unfiltered read, distinct from enrichment.js's extractNoteTexts()
// filtering/collapsing) inline as one section among several, without a
// second copy of the fetch effect.
import { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Alert, Divider } from '@mui/material'
import { fetchOrderNotes } from './api.js'

// conversationId/erpOrderId: which document's notes to fetch.
// onCountChange: optional — called with notes.length once the fetch
// resolves, so a caller that wants a "Notes (N)" heading (DocumentDetailWindow)
// can render the count without a second fetch of its own. OrderNotesWindow
// doesn't pass this — its window title already names the document.
export default function NotesList({ conversationId, erpOrderId, onCountChange }) {
  const [notes, setNotes] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    fetchOrderNotes(conversationId, erpOrderId)
      .then((d) => {
        if (!active) return
        const list = d.notes ?? []
        setNotes(list)
        onCountChange?.(list.length)
      })
      .catch((e) => { if (active) setError(e.message ?? 'Could not load notes.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCountChange is a setter, stable across renders; including it would re-fetch on every parent render.
  }, [conversationId, erpOrderId])

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!loading && error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}
      {!loading && !error && notes?.length === 0 && (
        <Typography variant="body2" color="text.secondary">No notes found.</Typography>
      )}
      {!loading && !error && notes?.map((n, i) => (
        <Box key={i}>
          {i > 0 && <Divider sx={{ my: 1.5 }} />}
          <Box sx={n.importantNoteFlag ? { borderLeft: 3, borderColor: 'error.main', pl: 1 } : undefined}>
            {n.header && (
              <Typography variant="caption" color="text.secondary" display="block">{n.header}</Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontWeight: n.importantNoteFlag ? 700 : 400 }}>
              {n.noteText}
            </Typography>
          </Box>
        </Box>
      ))}
    </>
  )
}
